import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Heart, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useWishlist } from "@/lib/wishlist";
import { resolveImage } from "@/lib/product-images";
import { formatPrice } from "@/lib/format";

export const Route = createFileRoute("/wishlist")({
  head: () => ({
    meta: [
      { title: "My Wishlist — Jul's Skin" },
      { name: "description", content: "Products you've saved on Jul's Skin for later." },
      { property: "og:title", content: "My Wishlist — Jul's Skin" },
      { property: "og:url", content: "https://julsskin.shop/wishlist" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://julsskin.shop/wishlist" }],
  }),
  component: WishlistPage,
});

function WishlistPage() {
  const { list, remove } = useWishlist();
  const { data: products } = useQuery({
    queryKey: ["wishlist-products", list.join(",")],
    enabled: list.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .in("id", list);
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <div className="mx-auto w-full max-w-5xl px-4 py-8 flex-1">
        <div className="flex items-center gap-2 mb-6">
          <Heart className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">My Wishlist</h1>
          <span className="text-sm text-muted-foreground">({list.length})</span>
        </div>

        {list.length === 0 ? (
          <div className="bg-card rounded-lg p-10 text-center">
            <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-4">Your wishlist is empty.</p>
            <Link to="/shop" className="inline-block bg-primary text-primary-foreground px-6 py-2.5 rounded-md font-medium text-sm">
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {(products ?? []).map((p) => (
              <div key={p.id} className="bg-card rounded-md overflow-hidden border border-border/60">
                <Link to="/product/$slug" params={{ slug: p.slug }}>
                  <div className="aspect-square bg-muted">
                    <img src={resolveImage(p.slug, p.image_url, 400)} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                </Link>
                <div className="p-2">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-sm font-bold text-foreground mt-0.5">{formatPrice(p.price_cents)}</p>
                  <button
                    onClick={() => remove(p.id)}
                    className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-destructive border border-border rounded py-1.5 hover:bg-muted"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
